import './common/styles.css';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams, useLocation, Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Footer, { FooterCompact } from './components/Layout/Footer';
import ScrollToTop from './utils/ScrollToTop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Header from './components/Layout/Header';
import HelloBar from './components/Layout/HelloBar';
import BackToTop from './components/shared/BackToTop';

// Maya lazy-loaded + deferred until idle — keeps axios + chat UI out of critical path
const MayaChatWidgetLazy = lazy(() => import('./components/Maya/MayaChatWidget'));
import ErrorBoundary from './components/shared/ErrorBoundary';
import LocaleLayout from './components/Layout/LocaleLayout';
import { SUPPORTED_LANGS } from './routes/routeMap';

// ─── Eager-loaded: HomePage is the LCP page — must be available
//     synchronously for hydrateRoot to match prerendered HTML. ─────
import HomePage from './pages/HomePage';

// ─── Lazy-loaded pages (route-based code splitting) ───────────────
const BlogPage = lazy(() => import('./pages/BlogPage'));
const CreditCardsPage = lazy(() => import('./pages/CreditCardsPage'));
const FinanclopediaPage = lazy(() => import('./pages/FinanclopediaPage'));
const FinanclopediaEntryPage = lazy(() => import('./pages/FinanclopediaEntryPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const Error404Page = lazy(() => import('./pages/Error404Page'));
const BudgetPlanner = lazy(() => import('./features/budgetPlanner'));
const MortgageCalculator = lazy(() => import('./components/FinancialTools/MortgageCalculator/MortgageCalculator'));
const CalculadoraRRSPPage = lazy(() => import('./pages/CalculadoraRRSPPage'));
const MortgagesPage = lazy(() => import('./pages/MortgagesPage'));
const BankingPage = lazy(() => import('./pages/BankingPage'));
const FinancialToolsPage = lazy(() => import('./pages/FinancialToolsPage'));
const EmpiezaAquiPage = lazy(() => import('./pages/EmpiezaAquiPage'));
const PostDetailPage = lazy(() => import('./pages/PostDetailPage'));
const SobreNosotrosPage = lazy(() => import('./pages/SobreNosotrosPage'));
const ComoMonetizamosPage = lazy(() => import('./pages/ComoMonetizamosPage'));
const CalendarioFiscalPage = lazy(() => import('./pages/CalendarioFiscalPage'));
const QuebecPage = lazy(() => import('./pages/QuebecPage'));
const PoliticaPrivacidadPage = lazy(() => import('./pages/PoliticaPrivacidadPage'));
const TerminosDeUsoPage = lazy(() => import('./pages/TerminosDeUsoPage'));
const T4ExplicadoPage = lazy(() => import('./pages/Blog/T4ExplicadoPage'));
const SolidaryTaxCreditPage = lazy(() => import('./pages/Blog/SolidaryTaxCreditPage'));
const GuiaImpuestosQuebecPage = lazy(() => import('./pages/Blog/GuiaImpuestosQuebecPage'));
const RRSPDeadlinePage = lazy(() => import('./pages/Blog/RRSPDeadlinePage'));
const CRAvsRevenuQuebecPage = lazy(() => import('./pages/Blog/CRAvsRevenuQuebecPage'));
const ConstruirCreditoPage = lazy(() => import('./pages/Blog/ConstruirCreditoPage'));
const TarjetasNewcomersPage = lazy(() => import('./pages/Blog/TarjetasNewcomersPage'));
const CuentasSinFeesPage = lazy(() => import('./pages/Blog/CuentasSinFeesPage'));
const TFSAvsRRSPvsFHSAPage = lazy(() => import('./pages/Blog/TFSAvsRRSPvsFHSAPage'));
const PrimeraHipotecaPage = lazy(() => import('./pages/Blog/PrimeraHipotecaPage'));
const CostoVidaMontrealPage = lazy(() => import('./pages/Blog/CostoVidaMontrealPage'));
const RbcVsTdPage = lazy(() => import('./pages/Comparar/RbcVsTdPage'));
const ScotiabankVsBmoPage = lazy(() => import('./pages/Comparar/ScotiabankVsBmoPage'));
const RbcVsDesjardinsPage = lazy(() => import('./pages/Comparar/RbcVsDesjardinsPage'));
const MejoresBancosPage = lazy(() => import('./pages/Comparar/MejoresBancosPage'));
const ComparadorCuentasPage = lazy(() => import('./pages/ComparadorCuentasPage'));
const ComparadorHipotecasPage = lazy(() => import('./pages/ComparadorHipotecasPage'));
const ComparadorRemesasPage = lazy(() => import('./pages/ComparadorRemesasPage'));
const CuentasRegistradasPage = lazy(() => import('./pages/CuentasRegistradasPage'));
const ComparadorSegurosPage = lazy(() => import('./pages/ComparadorSegurosPage'));
const GICRatesPage = lazy(() => import('./pages/GICRatesPage'));
const ComparadoresHubPage = lazy(() => import('./pages/ComparadoresHubPage'));
const SalaryCalculatorPage = lazy(() => import('./pages/SalaryCalculatorPage'));

// Internal ops dashboard — lazy-loaded, completely isolated from public routes
const MissionControlPage = lazy(() => import('./pages/ops/MissionControlPage'));

/** Centered spinner shown while lazy chunks load.
 *  data-prerender-loader lets PrerenderSignal wait for lazy content
 *  before dispatching render-event to Puppeteer. */
const PageLoader = () => (
  <Box data-prerender-loader sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress sx={{ color: '#1E3A5F' }} />
  </Box>
);

/** Per-route Suspense wrapper — keeps hydration mismatches contained
 *  to the lazy route instead of blowing up the entire page tree. */
const Lz = (C) => <Suspense fallback={<PageLoader />}><C /></Suspense>;

/** Defers Maya mount until the main thread is idle — keeps it out of critical rendering path.
 *  Skips rendering entirely on internal routes (/ops). */
function DeferredMaya() {
  const { pathname } = useLocation();
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;
    const cb = () => setReady(true);
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(cb);
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(cb, 3500);
    return () => clearTimeout(id);
  }, []);
  if (!ready || pathname === '/ops') return null;
  return <Suspense fallback={null}><MayaChatWidgetLazy /></Suspense>;
}

const COMPACT_FOOTER_PATHS = ['/empieza-aqui', '/get-started', '/commencer'];
const useIsCompactPage = () => {
  const { pathname } = useLocation();
  return COMPACT_FOOTER_PATHS.some((p) => pathname.includes(p));
};
const AppFooter = () => useIsCompactPage() ? <FooterCompact /> : <Footer />;

const AppShell = ({ children }) => {
  const isCompact = useIsCompactPage();
  const content = children || <Outlet />;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <HelloBar />
      <Header />
      <ErrorBoundary>
        <Box
          component="main"
          id="main-content"
          role="main"
          sx={{ flex: 1, ...(isCompact && { display: 'flex', flexDirection: 'column' }) }}
        >
          {content}
        </Box>
      </ErrorBoundary>
      <AppFooter />
    </Box>
  );
};

/**
 * Detects preferred language from localStorage or browser and redirects to /:lng/
 * Default 'es' matches prerender — useEffect reads client-only APIs after hydration.
 */
function RootRedirect() {
  const [lng, setLng] = React.useState('es');

  React.useEffect(() => {
    const savedLang = localStorage.getItem('fm_lang');
    const browserLang = navigator.language?.slice(0, 2);
    const detected = savedLang && SUPPORTED_LANGS.includes(savedLang)
      ? savedLang
      : SUPPORTED_LANGS.includes(browserLang) ? browserLang : 'es';
    if (detected !== 'es') setLng(detected);
  }, []);

  return <Navigate to={`/${lng}/`} replace />;
}

/**
 * All the locale-aware child routes (rendered inside LocaleLayout's <Outlet />).
 * Each page is registered under all its per-language slugs.
 */
function LocaleRoutes() {
  return (
    <>
      {/* Home — eager import, NO Suspense boundary → hydration matches prerender */}
      <Route index element={<HomePage />} />

      {/* Get Started / Empieza Aquí */}
      <Route path="empieza-aqui" element={Lz(EmpiezaAquiPage)} />
      <Route path="get-started" element={Lz(EmpiezaAquiPage)} />
      <Route path="commencer" element={Lz(EmpiezaAquiPage)} />

      {/* Credit Cards */}
      <Route path="tarjetas-de-credito" element={Lz(CreditCardsPage)} />
      <Route path="credit-cards" element={Lz(CreditCardsPage)} />
      <Route path="cartes-de-credit" element={Lz(CreditCardsPage)} />

      {/* Banking */}
      <Route path="banca" element={Lz(BankingPage)} />
      <Route path="banking" element={Lz(BankingPage)} />
      <Route path="banque" element={Lz(BankingPage)} />

      {/* Mortgages */}
      <Route path="hipotecas" element={Lz(MortgagesPage)} />
      <Route path="mortgages" element={Lz(MortgagesPage)} />
      <Route path="hypotheques" element={Lz(MortgagesPage)} />

      {/* Comparators Hub */}
      <Route path="comparadores" element={Lz(ComparadoresHubPage)} />
      <Route path="comparators" element={Lz(ComparadoresHubPage)} />
      <Route path="comparateurs" element={Lz(ComparadoresHubPage)} />

      {/* Tools */}
      <Route path="herramientas" element={Lz(FinancialToolsPage)} />
      <Route path="tools" element={Lz(FinancialToolsPage)} />
      <Route path="outils" element={Lz(FinancialToolsPage)} />

      {/* Mortgage Calculator */}
      <Route path="calculadora-hipoteca" element={Lz(MortgageCalculator)} />
      <Route path="mortgage-calculator" element={Lz(MortgageCalculator)} />
      <Route path="calculatrice-hypothecaire" element={Lz(MortgageCalculator)} />

      {/* Budget Planner */}
      <Route path="budget-planner" element={Lz(BudgetPlanner)} />
      <Route path="planificateur-budget" element={Lz(BudgetPlanner)} />

      {/* RRSP vs TFSA Calculator */}
      <Route path="herramientas/calculadora-rrsp-vs-tfsa" element={Lz(CalculadoraRRSPPage)} />
      <Route path="tools/rrsp-vs-tfsa-calculator" element={Lz(CalculadoraRRSPPage)} />
      <Route path="outils/calculatrice-reer-vs-celi" element={Lz(CalculadoraRRSPPage)} />

      {/* Salary Calculator */}
      <Route path="herramientas/calculadora-salario-neto" element={Lz(SalaryCalculatorPage)} />
      <Route path="tools/salary-calculator" element={Lz(SalaryCalculatorPage)} />
      <Route path="outils/calculatrice-salaire-net" element={Lz(SalaryCalculatorPage)} />

      {/* Financlopedia */}
      <Route path="financlopedia" element={Lz(FinanclopediaPage)} />
      <Route path="financlopedia/:slug" element={Lz(FinanclopediaEntryPage)} />

      {/* Blog */}
      <Route path="blog" element={Lz(BlogPage)} />
      <Route path="blogue" element={Lz(BlogPage)} />

      {/* Post → Blog redirect (legacy slug-based) */}
      <Route path="post/:slug" element={<PostToBlogRedirect />} />

      {/* Blog catch-all for posts rendered via postsData (3 unique articles) */}
      <Route path="blog/:slug" element={Lz(PostDetailPage)} />
      <Route path="blogue/:slug" element={Lz(PostDetailPage)} />

      {/* Contact */}
      <Route path="contacto" element={Lz(ContactUsPage)} />
      <Route path="contact" element={Lz(ContactUsPage)} />

      {/* About */}
      <Route path="sobre-nosotros" element={Lz(SobreNosotrosPage)} />
      <Route path="about-us" element={Lz(SobreNosotrosPage)} />
      <Route path="a-propos" element={Lz(SobreNosotrosPage)} />

      {/* How We Monetize */}
      <Route path="como-monetizamos" element={Lz(ComoMonetizamosPage)} />
      <Route path="how-we-monetize" element={Lz(ComoMonetizamosPage)} />
      <Route path="comment-nous-monetisons" element={Lz(ComoMonetizamosPage)} />

      {/* Tax Calendar */}
      <Route path="calendario-fiscal-2026" element={Lz(CalendarioFiscalPage)} />
      <Route path="tax-calendar-2026" element={Lz(CalendarioFiscalPage)} />
      <Route path="calendrier-fiscal-2026" element={Lz(CalendarioFiscalPage)} />

      {/* Quebec */}
      <Route path="quebec" element={Lz(QuebecPage)} />

      {/* Privacy */}
      <Route path="privacidad" element={Lz(PoliticaPrivacidadPage)} />
      <Route path="privacy-policy" element={Lz(PoliticaPrivacidadPage)} />
      <Route path="politique-confidentialite" element={Lz(PoliticaPrivacidadPage)} />

      {/* Terms */}
      <Route path="terminos-de-uso" element={Lz(TerminosDeUsoPage)} />
      <Route path="terms-of-use" element={Lz(TerminosDeUsoPage)} />
      <Route path="conditions-utilisation" element={Lz(TerminosDeUsoPage)} />

      {/* Account Comparator */}
      <Route path="comparar-cuentas" element={Lz(ComparadorCuentasPage)} />
      <Route path="compare-accounts" element={Lz(ComparadorCuentasPage)} />
      <Route path="comparateur-comptes" element={Lz(ComparadorCuentasPage)} />

      {/* Mortgage Comparator → redirect to /hipotecas#programas-newcomer */}
      <Route path="comparar-hipotecas" element={<MortgageComparatorRedirect />} />
      <Route path="compare-mortgages" element={<MortgageComparatorRedirect />} />
      <Route path="comparateur-hypotheques" element={<MortgageComparatorRedirect />} />

      {/* Remittance Comparator */}
      <Route path="comparar-remesas" element={Lz(ComparadorRemesasPage)} />
      <Route path="compare-remittances" element={Lz(ComparadorRemesasPage)} />
      <Route path="comparateur-envois" element={Lz(ComparadorRemesasPage)} />

      {/* Registered Accounts */}
      <Route path="cuentas-registradas" element={Lz(CuentasRegistradasPage)} />
      <Route path="registered-accounts" element={Lz(CuentasRegistradasPage)} />
      <Route path="comptes-enregistres" element={Lz(CuentasRegistradasPage)} />

      {/* Insurance Comparator */}
      <Route path="comparar-seguros" element={Lz(ComparadorSegurosPage)} />
      <Route path="compare-insurance" element={Lz(ComparadorSegurosPage)} />
      <Route path="comparateur-assurances" element={Lz(ComparadorSegurosPage)} />

      {/* GIC Rates */}
      <Route path="tasas-gic" element={Lz(GICRatesPage)} />
      <Route path="gic-rates" element={Lz(GICRatesPage)} />
      <Route path="taux-gic" element={Lz(GICRatesPage)} />

      {/* Blog articles (dedicated pages) */}
      <Route path="blog/t4-explicado-espanol-impuestos-canada" element={Lz(T4ExplicadoPage)} />
      <Route path="blogue/t4-explicado-espanol-impuestos-canada" element={Lz(T4ExplicadoPage)} />
      <Route path="blog/solidarity-tax-credit-quebec-guia-completa" element={Lz(SolidaryTaxCreditPage)} />
      <Route path="blogue/solidarity-tax-credit-quebec-guia-completa" element={Lz(SolidaryTaxCreditPage)} />
      <Route path="blog/guia-impuestos-latinos-quebec-2026" element={Lz(GuiaImpuestosQuebecPage)} />
      <Route path="blogue/guia-impuestos-latinos-quebec-2026" element={Lz(GuiaImpuestosQuebecPage)} />
      <Route path="blog/rrsp-deadline-marzo-2026" element={Lz(RRSPDeadlinePage)} />
      <Route path="blogue/rrsp-deadline-marzo-2026" element={Lz(RRSPDeadlinePage)} />
      <Route path="blog/cra-vs-revenu-quebec-diferencias" element={Lz(CRAvsRevenuQuebecPage)} />
      <Route path="blogue/cra-vs-revenu-quebec-diferencias" element={Lz(CRAvsRevenuQuebecPage)} />
      <Route path="blog/construir-credito-canada-desde-cero" element={Lz(ConstruirCreditoPage)} />
      <Route path="blogue/construir-credito-canada-desde-cero" element={Lz(ConstruirCreditoPage)} />
      <Route path="blog/tarjetas-newcomers-que-piden-bancos" element={Lz(TarjetasNewcomersPage)} />
      <Route path="blogue/tarjetas-newcomers-que-piden-bancos" element={Lz(TarjetasNewcomersPage)} />
      <Route path="blog/mejores-cuentas-sin-fees-recien-llegados" element={Lz(CuentasSinFeesPage)} />
      <Route path="blogue/mejores-cuentas-sin-fees-recien-llegados" element={Lz(CuentasSinFeesPage)} />
      <Route path="blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes" element={Lz(TFSAvsRRSPvsFHSAPage)} />
      <Route path="blogue/tfsa-vs-rrsp-vs-fhsa-inmigrantes" element={Lz(TFSAvsRRSPvsFHSAPage)} />
      <Route path="blog/primera-hipoteca-canada-guia" element={Lz(PrimeraHipotecaPage)} />
      <Route path="blogue/primera-hipoteca-canada-guia" element={Lz(PrimeraHipotecaPage)} />
      <Route path="blog/costo-de-vida-montreal-2026" element={Lz(CostoVidaMontrealPage)} />
      <Route path="blogue/costo-de-vida-montreal-2026" element={Lz(CostoVidaMontrealPage)} />

      {/* Bank comparison pages */}
      <Route path="comparar/rbc-vs-td-recien-llegados" element={Lz(RbcVsTdPage)} />
      <Route path="comparar/scotiabank-vs-bmo-inmigrantes" element={Lz(ScotiabankVsBmoPage)} />
      <Route path="comparar/rbc-vs-desjardins-quebec" element={Lz(RbcVsDesjardinsPage)} />
      <Route path="comparar/mejores-bancos-recien-llegados-canada-2026" element={Lz(MejoresBancosPage)} />

      {/* Legacy redirects within locale */}
      <Route path="primeros-pasos" element={<Navigate to="../empieza-aqui" replace />} />
      <Route path="credit-card-comparison-tool" element={<Navigate to="../tarjetas-de-credito" replace />} />
      <Route path="financial-tools" element={<Navigate to="../herramientas" replace />} />
      <Route path="glosario" element={<Navigate to="../financlopedia" replace />} />
      <Route path="glosario/:término" element={<Navigate to="../financlopedia" replace />} />
      <Route path="Financlopedia" element={<Navigate to="../financlopedia" replace />} />
      <Route path="finance/:topicId" element={Lz(FinanclopediaEntryPage)} />

      {/* Legacy numeric post redirects → blog */}
      <Route path="post/1" element={<Navigate to="../blog/construir-credito-canada-desde-cero" replace />} />
      <Route path="post/2" element={<Navigate to="../blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes" replace />} />
      <Route path="post/3" element={<Navigate to="../blog/enviar-dinero-latinoamerica-canada" replace />} />
      <Route path="post/4" element={<Navigate to="../blog/primera-hipoteca-canada-guia" replace />} />
      <Route path="post/5" element={<Navigate to="../blog/errores-financieros-recien-llegados-canada" replace />} />
      <Route path="post/6" element={<Navigate to="../blog/t4-explicado-espanol-impuestos-canada" replace />} />
      <Route path="post/7" element={<Navigate to="../blog/credito-solidaridad-quebec-inmigrantes" replace />} />

      {/* 404 within locale */}
      <Route path="*" element={Lz(Error404Page)} />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Internal ops dashboard — localhost only, not for production */}
          <Route path="/ops" element={
            ['localhost', '127.0.0.1'].includes(window.location.hostname)
              ? Lz(MissionControlPage)
              : <Navigate to="/" replace />
          } />

          {/* All public routes wrapped in AppShell layout */}
          <Route element={<AppShell />}>
            {/* Root → auto-detect language and redirect */}
            <Route path="/" element={<RootRedirect />} />

            {/* Legacy paths without language prefix → redirect to /es/... */}
            <Route path="/tarjetas-de-credito" element={<Navigate to="/es/tarjetas-de-credito" replace />} />
            <Route path="/credit-cards" element={<Navigate to="/es/tarjetas-de-credito" replace />} />
            <Route path="/banca" element={<Navigate to="/es/banca" replace />} />
            <Route path="/banking" element={<Navigate to="/es/banca" replace />} />
            <Route path="/hipotecas" element={<Navigate to="/es/hipotecas" replace />} />
            <Route path="/mortgages" element={<Navigate to="/es/hipotecas" replace />} />
            <Route path="/herramientas" element={<Navigate to="/es/herramientas" replace />} />
            <Route path="/financial-tools" element={<Navigate to="/es/herramientas" replace />} />
            <Route path="/blog" element={<Navigate to="/es/blog" replace />} />
            <Route path="/financlopedia" element={<Navigate to="/es/financlopedia" replace />} />
            <Route path="/Financlopedia" element={<Navigate to="/es/financlopedia" replace />} />
            <Route path="/contacto" element={<Navigate to="/es/contacto" replace />} />
            <Route path="/contact-us" element={<Navigate to="/es/contacto" replace />} />
            <Route path="/empieza-aqui" element={<Navigate to="/es/empieza-aqui" replace />} />
            <Route path="/primeros-pasos" element={<Navigate to="/es/empieza-aqui" replace />} />
            <Route path="/sobre-nosotros" element={<Navigate to="/es/sobre-nosotros" replace />} />
            <Route path="/como-monetizamos" element={<Navigate to="/es/como-monetizamos" replace />} />
            <Route path="/calendario-fiscal-2026" element={<Navigate to="/es/calendario-fiscal-2026" replace />} />
            <Route path="/quebec" element={<Navigate to="/es/quebec" replace />} />
            <Route path="/privacidad" element={<Navigate to="/es/privacidad" replace />} />
            <Route path="/terminos-de-uso" element={<Navigate to="/es/terminos-de-uso" replace />} />
            <Route path="/calculadora-hipoteca" element={<Navigate to="/es/calculadora-hipoteca" replace />} />
            <Route path="/mortgage-calculator" element={<Navigate to="/es/calculadora-hipoteca" replace />} />
            <Route path="/budget-planner" element={<Navigate to="/es/budget-planner" replace />} />
            <Route path="/credit-card-comparison-tool" element={<Navigate to="/es/tarjetas-de-credito" replace />} />
            <Route path="/comparadores" element={<Navigate to="/es/comparadores" replace />} />
            <Route path="/comparar-cuentas" element={<Navigate to="/es/comparar-cuentas" replace />} />
            <Route path="/comparar-hipotecas" element={<Navigate to="/es/hipotecas#programas-newcomer" replace />} />
            <Route path="/comparar-remesas" element={<Navigate to="/es/comparar-remesas" replace />} />
            <Route path="/cuentas-registradas" element={<Navigate to="/es/cuentas-registradas" replace />} />
            <Route path="/comparar-seguros" element={<Navigate to="/es/comparar-seguros" replace />} />
            <Route path="/tasas-gic" element={<Navigate to="/es/tasas-gic" replace />} />
            <Route path="/glosario" element={<Navigate to="/es/financlopedia" replace />} />
            <Route path="/glosario/:término" element={<Navigate to="/es/financlopedia" replace />} />

            {/* Legacy blog articles without prefix */}
            <Route path="/blog/t4-explicado-espanol-impuestos-canada" element={<Navigate to="/es/blog/t4-explicado-espanol-impuestos-canada" replace />} />
            <Route path="/blog/solidarity-tax-credit-quebec-guia-completa" element={<Navigate to="/es/blog/solidarity-tax-credit-quebec-guia-completa" replace />} />
            <Route path="/blog/guia-impuestos-latinos-quebec-2026" element={<Navigate to="/es/blog/guia-impuestos-latinos-quebec-2026" replace />} />
            <Route path="/blog/rrsp-deadline-marzo-2026" element={<Navigate to="/es/blog/rrsp-deadline-marzo-2026" replace />} />
            <Route path="/blog/cra-vs-revenu-quebec-diferencias" element={<Navigate to="/es/blog/cra-vs-revenu-quebec-diferencias" replace />} />
            <Route path="/blog/construir-credito-canada-desde-cero" element={<Navigate to="/es/blog/construir-credito-canada-desde-cero" replace />} />
            <Route path="/blog/tarjetas-newcomers-que-piden-bancos" element={<Navigate to="/es/blog/tarjetas-newcomers-que-piden-bancos" replace />} />
            <Route path="/blog/mejores-cuentas-sin-fees-recien-llegados" element={<Navigate to="/es/blog/mejores-cuentas-sin-fees-recien-llegados" replace />} />
            <Route path="/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes" element={<Navigate to="/es/blog/tfsa-vs-rrsp-vs-fhsa-inmigrantes" replace />} />
            <Route path="/blog/primera-hipoteca-canada-guia" element={<Navigate to="/es/blog/primera-hipoteca-canada-guia" replace />} />
            <Route path="/costo-de-vida-montreal-2026" element={<Navigate to="/es/blog/costo-de-vida-montreal-2026" replace />} />
            <Route path="/blog/costo-de-vida-montreal-2026" element={<Navigate to="/es/blog/costo-de-vida-montreal-2026" replace />} />

            {/* Legacy comparar pages without prefix */}
            <Route path="/comparar/rbc-vs-td-recien-llegados" element={<Navigate to="/es/comparar/rbc-vs-td-recien-llegados" replace />} />
            <Route path="/comparar/scotiabank-vs-bmo-inmigrantes" element={<Navigate to="/es/comparar/scotiabank-vs-bmo-inmigrantes" replace />} />
            <Route path="/comparar/rbc-vs-desjardins-quebec" element={<Navigate to="/es/comparar/rbc-vs-desjardins-quebec" replace />} />
            <Route path="/comparar/mejores-bancos-recien-llegados-canada-2026" element={<Navigate to="/es/comparar/mejores-bancos-recien-llegados-canada-2026" replace />} />

            {/* Legacy post routes */}
            <Route path="/post/:slug" element={<LegacyPostRedirect />} />
            <Route path="/financlopedia/:slug" element={<LegacyFinanclopediaRedirect />} />
            <Route path="/finance/:topicId" element={<LegacyFinanclopediaRedirect />} />

            {/* Locale-prefixed routes */}
            <Route path="/:lng" element={<LocaleLayout />}>
              {LocaleRoutes()}
            </Route>

            {/* Global 404 */}
            <Route path="*" element={Lz(Error404Page)} />
          </Route>
        </Routes>
        <DeferredMaya />
        <BackToTop />
      </Router>
    </HelmetProvider>
  );
}

/** Maps legacy /post/ slugs to their canonical /blog/ slugs */
const POST_TO_BLOG = {
  'construir-historial-crediticio-canada': 'construir-credito-canada-desde-cero',
  't4-explicado-inmigrantes-canada': 't4-explicado-espanol-impuestos-canada',
  'tfsa-vs-rrsp-inmigrantes-canada': 'tfsa-vs-rrsp-vs-fhsa-inmigrantes',
  'primera-hipoteca-canada-recien-llegados': 'primera-hipoteca-canada-guia',
};

/** Redirects legacy /post/:slug to /es/blog/:slug (server-level for crawlers, client fallback here) */
function LegacyPostRedirect() {
  const slug = window.location.pathname.split('/post/')[1];
  const blogSlug = POST_TO_BLOG[slug] || slug;
  return <Navigate to={`/es/blog/${blogSlug}`} replace />;
}

/** Redirects /comparar-hipotecas → /hipotecas#programas-newcomer */
function MortgageComparatorRedirect() {
  const { lng } = useParams();
  return <Navigate to={`/${lng || 'es'}/hipotecas#programas-newcomer`} replace />;
}

/** Redirects /:lng/post/:slug → /:lng/blog/:slug (inside locale routes) */
function PostToBlogRedirect() {
  const { slug, lng } = useParams();
  const blogSlug = POST_TO_BLOG[slug] || slug;
  return <Navigate to={`/${lng || 'es'}/blog/${blogSlug}`} replace />;
}

/** Redirects legacy /financlopedia/:slug to /es/financlopedia/:slug */
function LegacyFinanclopediaRedirect() {
  const path = window.location.pathname;
  if (path.startsWith('/finance/')) {
    const topicId = path.split('/finance/')[1];
    return <Navigate to={`/es/financlopedia/${topicId}`} replace />;
  }
  const slug = path.split('/financlopedia/')[1];
  return <Navigate to={`/es/financlopedia/${slug}`} replace />;
}

export default App;
